import React, { useCallback, useMemo } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { toDayJs } from '~/services/TimeService';

import { ReopenImpact, ReopenRoom } from '~/types/reopen';
import { CatalogRoom, ExistingBlackoutDate, ExtractedDateRange } from '~/types/stopsell';

import { formatDateRange } from '~/utils/dateRangeUtils';
import { pluralizeToString } from '~/utils/stringUtils';

import AddDateSection from './AddDateSection';
import ReopenExtractedDateRow from './ReopenExtractedDateRow';

interface Props {
  room: ReopenRoom;
  localDates: Array<ExtractedDateRange>;
  impacts: Array<Array<ReopenImpact>>;
  catalogRooms: Array<CatalogRoom>;
  selectedRoomTypeId: string;
  blackoutDates: Array<ExistingBlackoutDate>;
  catalogRoomRates: Array<{ id: string; name: string }>;
  deselectedBlockIds: Set<string>;
  onChange: (dateIndex: number, value: boolean) => void;
  onToggleAll: (value: boolean) => void;
  onRoomTypeChange: (roomTypeId: string) => void;
  onEditDate: (dateId: string, newRange: { start: string; end: string }) => void;
  onDeleteDate: (dateId: string) => void;
  onAddExtractedDate: (start: string, end: string) => void;
  onBlockToggle: (blockId: string, selected: boolean) => void;
}

export default function ReopenRoomGroup({
  room,
  localDates,
  impacts,
  catalogRooms,
  selectedRoomTypeId,
  blackoutDates,
  catalogRoomRates,
  deselectedBlockIds,
  onChange,
  onToggleAll,
  onRoomTypeChange,
  onEditDate,
  onDeleteDate,
  onAddExtractedDate,
  onBlockToggle,
}: Props) {
  const selectableDates = localDates.filter((_, di) => (impacts[di]?.length ?? 0) > 0);
  const selectedCount = selectableDates.filter((date) => date.selected).length;
  const allSelected = selectableDates.length > 0 && selectedCount === selectableDates.length;
  const anySelected = selectedCount > 0;
  const indeterminate = anySelected && !allSelected;

  const handleToggleAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleAll(!allSelected);
    },
    [allSelected, onToggleAll],
  );

  const isMapped = !!selectedRoomTypeId;

  const today = useMemo(() => toDayJs(new Date().toISOString().slice(0, 10)), []);
  const futureBlackoutDates = blackoutDates
    .filter((d) => !toDayJs(d.end).isBefore(today, 'day'))
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  const existingLabel =
    futureBlackoutDates.length > 0 ? futureBlackoutDates.map((d) => formatDateRange(d.start, d.end)).join(', ') : null;

  return (
    <Accordion defaultExpanded variant="outlined" disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ bgcolor: 'grey.50' }}
        component="div"
        role="button"
        tabIndex={0}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, mr: 1 }}>
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            onChange={(e) => {
              e.stopPropagation();
              onToggleAll(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.5}>
            <Typography fontWeight={500} variant="body2">
              {room.name}
            </Typography>
            {catalogRooms.length > 0 && (
              <Select
                size="small"
                displayEmpty
                value={selectedRoomTypeId}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onRoomTypeChange(e.target.value)}
                error={!isMapped}
                sx={{ fontSize: 12, maxWidth: 320 }}
              >
                <MenuItem value="" disabled>
                  <Typography variant="body2" color="text.disabled">
                    Select room type…
                  </Typography>
                </MenuItem>
                {catalogRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            {existingLabel && (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <LockOpenOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Existing blackouts: {existingLabel}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Chip label={pluralizeToString('date range', localDates.length)} size="small" sx={{ bgcolor: 'grey.200' }} />
          <Box>
            <Button size="small" onClick={handleToggleAll} disabled={selectableDates.length === 0}>
              {allSelected ? 'Deselect all' : 'Select all dates'}
            </Button>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack>
          {localDates.map((date, dateIndex) => (
            <ReopenExtractedDateRow
              key={date.id}
              date={date}
              dateIndex={dateIndex}
              impacts={impacts[dateIndex] ?? []}
              catalogRoomRates={catalogRoomRates}
              deselectedBlockIds={deselectedBlockIds}
              disabled={(impacts[dateIndex]?.length ?? 0) === 0}
              onToggle={onChange}
              onEdit={onEditDate}
              onDelete={onDeleteDate}
              onBlockToggle={onBlockToggle}
            />
          ))}
        </Stack>
        <Box sx={{ px: 5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <AddDateSection onAdd={onAddExtractedDate} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
